import { useState, useEffect } from "react";
import { FaInfoCircle } from "react-icons/fa";
import { Tooltip } from "react-tooltip";
import waitonCreate from "../../../assets/load.gif";
import CustomerDropDownSelector from "../controls/CustomerDropDownSelector";
import AgreementDropDownSelector from "../controls/AgreementDropDownSelector";
import { fixDateFormat } from "../Utils.js";

const ACCOUNT_DISABLED_MSG =
  "Användarens konto har inaktiverats på grund av inaktivitet. Du behöver lägga ett Efecte-ärende för att aktivera användarens konto igen. Kom ihåg att inkludera information om vilken användare det gäller.";

const InviteUser = ({ getInstance, customers }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [customerAgreements, setCustomerAgreements] = useState([]);
  const [agreementId, setAgreementId] = useState("NA");
  const [msgText, setMsgText] = useState("");
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    setCustomerAgreements([]);
    setAgreementId("NA");

    if (customerId && customerId !== "NA") {
      getInstance("AGREEMENTS", customerId).then((agreements) => {
        setCustomerAgreements(agreements || []);
      });
    }
  }, [customerId, getInstance]);

  const sendInvitation = async () => {
    setIsLoading(true);
    setConfirmState(null);
    setMsgText("");

    const userData = {
      name,
      email,
      phone,
      customerId,
      agreements: agreementId,
    };

    try {
      const result = await getInstance("SENDINVITE", userData);
      setMsgText(result);
    } catch (error) {
      setMsgText("Skicka inbjudning misslyckades. Kontakta support.");
      console.error("Error sending invite:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const btnSendEmail = async () => {
    setIsLoading(true);
    setMsgText("");

    try {
      const status = await getInstance("CHECKINVITE", email);
      if (status?.Exists) {
        setConfirmState(status);
        setIsLoading(false);
        return;
      }
    } catch (error) {
      setMsgText("Kunde inte verifiera användaren. Kontakta support.");
      setIsLoading(false);
      return;
    }

    await sendInvitation();
  };

  const cancelInvitation = () => {
    setConfirmState(null);
    setMsgText("");
  };

  function checkValues() {
    let ret = false;
    if (
      phone === "" ||
      name === "" ||
      customerId === "" ||
      customerId === "NA" ||
      agreementId === "NA" ||
      email === ""
    ) {
      ret = true;
    } else {
      if (/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(email)) {
        ret = false;
      } else {
        ret = true;
      }
      if (/^\+?[0-9]*$/.test(phone)) {
        ret = false;
      } else {
        ret = true;
      }
    }
    return ret;
  }

  if (confirmState) {
    const lastInvite = confirmState.InvitationSentAt
      ? fixDateFormat(confirmState.InvitationSentAt)
      : null;

    return (
      <div className="divTab">
        <div className="divInviteConfirm">
          {!confirmState.AccountEnabled ? (
            <>
              <p className="pInviteWarning">{ACCOUNT_DISABLED_MSG}</p>
              <div className="divInviteButtons">
                <button className="btn" onClick={cancelInvitation}>
                  Avbryt
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="pInviteWarning">
                Användaren <strong>{email}</strong> finns redan i systemet
                {lastInvite
                  ? ` och fick en inbjudan senast ${lastInvite}.`
                  : "."}{" "}
                Vill du skicka en ny inbjudan? Kund- och avtalsinformation
                kommer att uppdateras för användaren.
              </p>
              <div className="divInviteButtons">
                <button className="btn" onClick={sendInvitation}>
                  Skicka ny inbjudan
                </button>
                <button className="btn" onClick={cancelInvitation}>
                  Avbryt
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="divTab">
      <table className="tableInvite">
        <tbody>
          <tr>
            <td className="tdSpace" style={{ whiteSpace: "nowrap" }}>
              För och efternamn
            </td>
            <td className="tdRight tdSpace">
              <input
                type="text"
                className="txtAdmin"
                onChange={(e) => setName(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <td className="tdSpace">
              Telefon{" "}
              <FaInfoCircle
                id={`phone`}
                data-tooltip-html="Användarens telefonnummer, <br />skriv i formatet 0701236547 eller +46701236547."
              />
              <Tooltip anchorId={`phone`} multiline={true} data-html={true} />
            </td>
            <td className="tdRight tdSpace">
              <input
                type="text"
                className="txtAdmin"
                onChange={(e) => setPhone(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            <td className="tdSpace">
              Email{" "}
              <FaInfoCircle
                id={`email`}
                data-tooltip-content="Användarens e-post adress."
              />
              <Tooltip anchorId={`email`} multiline={true} data-html={true} />
            </td>
            <td className="tdRight tdSpace">
              <input
                type="text"
                className="txtAdmin"
                onChange={(e) => setEmail(e.target.value)}
              />
            </td>
          </tr>
          <tr>
            {" "}
            <td className="tdSpace">
              Kundnummer{" "}
              <FaInfoCircle
                id={`custno`}
                data-tooltip-content="Välj kund från listan."
              />
              <Tooltip anchorId={`custno`} multiline={true} data-html={true} />
            </td>
            <td className="tdRight tdSpace">
              <CustomerDropDownSelector
                headerText=""
                ddData={customers}
                customerId={customerId}
                setCustomerId={setCustomerId}
              />
            </td>
          </tr>
          <tr>
            {" "}
            <td className="tdSpace">
              Avtalsnummer{" "}
              <FaInfoCircle
                id={`agreements`}
                data-tooltip-content="Välj ett avtal kopplat till den valda kunden."
              />
              <Tooltip
                anchorId={`agreements`}
                multiline={true}
                data-html={true}
              />
            </td>
            <td className="tdRight tdSpace">
              <AgreementDropDownSelector
                headerText=""
                agreements={customerAgreements}
                agreementId={agreementId}
                setAgreementId={setAgreementId}
              />
            </td>
          </tr>
          <tr>
            <td> </td>
            <td className="tdRight tdSpace">
              {isLoading ? (
                <img
                  src={waitonCreate}
                  alt="loading..."
                  className="loadGifsmall"
                />
              ) : (
                msgText
              )}{" "}
              <button
                className="btn"
                onClick={btnSendEmail}
                disabled={checkValues()}
              >
                Skicka inbjudan
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default InviteUser;
